import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { formatCurrency } from '../../utils/currencyFormatter';
import { invoiceDesignTokens } from '../../utils/invoiceDesignTokens';

const formatOrderDate = (value, language) => {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleDateString(language, {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

const truncate = (value, maxLength) => (
  value.length > maxLength ? `${value.slice(0, maxLength)}…` : value
);

const joinDistinctProductNames = (orderItems) => {
  const names = Array.from(new Set((orderItems || [])
    .map((item) => item.productName)
    .filter(Boolean)));
  return names.length ? truncate(names.join(', '), 60) : '';
};

const sumOrderQuantity = (lineItem) => {
  const orderItems = lineItem.orderItems || [];
  if (orderItems.length === 0) return lineItem.quantity;
  return orderItems.reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
};

const headerCellSx = {
  color: 'text.secondary',
  fontSize: `${invoiceDesignTokens.fontSize.label}px`,
  textTransform: 'uppercase',
  letterSpacing: '0.6px',
  borderBottom: '1.5px solid',
  borderBottomColor: 'divider'
};

const bodyCellSx = { fontSize: `${invoiceDesignTokens.fontSize.body}px`, color: 'text.primary' };

// `selectable` adds a checkbox per order (create dialog only); the detail
// dialog renders the same table read-only. `stickyHeader` is for callers
// that put the table inside their own scroll container.
const OrderBasedInvoiceLineItemsTable = ({
  lineItems = [], currency, includeItemBreakdown = true, sx, stickyHeader = false,
  selectable = false, selectedOrderIds = [], onToggleOrder, onToggleAll
}) => {
  const { t, i18n } = useTranslation();

  const allOrderIds = lineItems.map((lineItem) => lineItem.sourceOrderId).filter(Boolean);
  const allSelected = allOrderIds.length > 0 && allOrderIds.every((id) => selectedOrderIds.includes(id));
  const someSelected = allOrderIds.some((id) => selectedOrderIds.includes(id));
  const headerCheckbox = selectable ? (
    <TableCell padding="checkbox" sx={headerCellSx}>
      <Checkbox
        size="small"
        checked={allSelected}
        indeterminate={someSelected && !allSelected}
        onChange={(e) => onToggleAll && onToggleAll(e.target.checked)}
        inputProps={{ 'aria-label': t('Select all orders', { defaultValue: 'Select all orders' }) }}
      />
    </TableCell>
  ) : null;
  const rowCheckbox = (lineItem, orderNumber) => (selectable ? (
    <TableCell padding="checkbox">
      <Checkbox
        size="small"
        checked={selectedOrderIds.includes(lineItem.sourceOrderId)}
        onChange={() => onToggleOrder && onToggleOrder(lineItem.sourceOrderId)}
        inputProps={{ 'aria-label': t('Select order {{n}}', { n: orderNumber, defaultValue: `Select order ${orderNumber}` }) }}
      />
    </TableCell>
  ) : null);

  if (!includeItemBreakdown) {
    return (
      <Table size="small" aria-label={t('Line items')} sx={sx} stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            {headerCheckbox}
            <TableCell sx={headerCellSx}>{t('Order Number')}</TableCell>
            <TableCell sx={headerCellSx}>{t('Order Date')}</TableCell>
            <TableCell sx={headerCellSx}>{t('Description')}</TableCell>
            <TableCell sx={headerCellSx} align="right">{t('Total Qty')}</TableCell>
            <TableCell sx={headerCellSx} align="right">{t('Order Total')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {lineItems.map((lineItem, orderIndex) => {
            const orderKey = lineItem.sourceOrderId || lineItem.sourceOrderNumber || orderIndex;
            const orderNumber = lineItem.sourceOrderNumber || lineItem.description || '';

            return (
              <TableRow
                key={orderKey}
                sx={{ '& > *': { borderBottom: '1px solid', borderBottomColor: 'divider' } }}
              >
                {rowCheckbox(lineItem, orderNumber)}
                <TableCell sx={bodyCellSx}>
                  {orderNumber}
                </TableCell>
                <TableCell sx={{ ...bodyCellSx, color: 'text.secondary' }}>
                  {formatOrderDate(lineItem.orderDate, i18n.language)}
                </TableCell>
                <TableCell sx={{ color: 'text.secondary', fontSize: `${invoiceDesignTokens.fontSize.description}px` }}>
                  {joinDistinctProductNames(lineItem.orderItems)}
                </TableCell>
                <TableCell align="right" sx={bodyCellSx}>{sumOrderQuantity(lineItem)}</TableCell>
                <TableCell align="right" sx={bodyCellSx}>
                  {formatCurrency(lineItem.subtotal, currency, i18n.language)}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }

  return (
    <Table size="small" aria-label={t('Line items')} sx={sx} stickyHeader={stickyHeader}>
      <TableHead>
        <TableRow>
          {headerCheckbox}
          <TableCell sx={headerCellSx}>{t('Description')}</TableCell>
          <TableCell sx={headerCellSx} align="right">{t('Quantity')}</TableCell>
          <TableCell sx={headerCellSx} align="right">{t('Unit price')}</TableCell>
          <TableCell sx={headerCellSx} align="right">{t('Discount')}</TableCell>
          <TableCell sx={headerCellSx} align="right">{t('Subtotal')}</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {lineItems.map((lineItem, orderIndex) => {
          const orderKey = lineItem.sourceOrderId || lineItem.sourceOrderNumber || orderIndex;
          const orderLabel = lineItem.description || lineItem.sourceOrderNumber || '';

          return (
            <Fragment key={orderKey}>
              <TableRow
                sx={{
                  '& > *': {
                    borderTop: '1px solid',
                    borderTopColor: 'divider',
                    pt: '14px'
                  }
                }}
              >
                {rowCheckbox(lineItem, lineItem.sourceOrderNumber || orderLabel)}
                <TableCell component="th" scope="rowgroup" colSpan={4}>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', flexWrap: 'wrap', gap: 1.5 }}>
                    <Typography
                      component="span"
                      sx={{ fontSize: `${invoiceDesignTokens.fontSize.groupHeader}px` }}
                      color="text.primary"
                    >
                      {orderLabel}
                    </Typography>
                    {lineItem.orderDate && (
                      <Typography
                        component="span"
                        sx={{ fontSize: `${invoiceDesignTokens.fontSize.meta}px` }}
                        color="text.secondary"
                      >
                        {formatOrderDate(lineItem.orderDate, i18n.language)}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell align="right" sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                  {formatCurrency(lineItem.subtotal, currency, i18n.language)}
                </TableCell>
              </TableRow>
              {(lineItem.orderItems || []).map((orderItem, itemIndex) => (
                <TableRow
                  key={`${orderKey}-${orderItem.productId || 'item'}-${itemIndex}`}
                  sx={{ '& > *': { borderBottom: '1px solid', borderBottomColor: 'divider' } }}
                >
                  {selectable && <TableCell padding="checkbox" />}
                  <TableCell sx={{ pl: '20px' }}>
                    <Typography sx={{ fontSize: `${invoiceDesignTokens.fontSize.body}px` }} color="text.primary">
                      {orderItem.productName}
                    </Typography>
                  </TableCell>
                  <TableCell align="right" sx={bodyCellSx}>{orderItem.quantity}</TableCell>
                  <TableCell align="right" sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                    {formatCurrency(orderItem.unitPrice, currency, i18n.language)}
                  </TableCell>
                  <TableCell align="right" sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                    {Number(orderItem.discount)
                      ? formatCurrency(orderItem.discount, currency, i18n.language)
                      : '—'}
                  </TableCell>
                  <TableCell align="right" sx={{ ...bodyCellSx, whiteSpace: 'nowrap' }}>
                    {formatCurrency(orderItem.subtotal, currency, i18n.language)}
                  </TableCell>
                </TableRow>
              ))}
            </Fragment>
          );
        })}
      </TableBody>
    </Table>
  );
};

export default OrderBasedInvoiceLineItemsTable;
